import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth/AuthContext';
import { useApi } from '../lib/apiClient';
import {
    Button,
    Skeleton,
} from '@pagespeedify/ui';
import { PlusCircle, Image as ImageIcon, Command } from 'lucide-react';
import { ImageOptimizationForm } from '../components/image-optimization/ImageOptimizationForm';
import { ImageOptimizationTaskView } from '../components/image-optimization/ImageOptimizationTaskView';
import { ImageTaskQuickNav } from '../components/image-optimization/ImageTaskQuickNav';
import { useDeleteQueue } from '../components/tasks/DeleteQueueContext';
import { Task } from '../types';
import { extractTasksArray } from '../lib/tasksResponse';

export function ImageOptimizationPage() {
    const { user, isLoading: authIsLoading } = useAuth();
    const apiClient = useApi();
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    // const { addTaskToDeleteQueue } = useDeleteQueue(); // Removed duplicate

    // View state: similar to MonitoringPage pattern
    const [showCreateForm, setShowCreateForm] = useState(false);
    // View state: initialized from URL/State to prevent race conditions
    const [selectedTaskId, setSelectedTaskId] = useState<string | null>(() => {
        const state = location.state as { taskId?: string; selectedTask?: Task };
        const searchParams = new URLSearchParams(location.search);
        const taskIdFromUrl = searchParams.get('taskId');

        if (state?.taskId) return state.taskId;
        if (taskIdFromUrl) return taskIdFromUrl;
        if (state?.selectedTask?.type === 'image_optimization') return state.selectedTask.id;

        return null;
    });
    const [isQuickNavOpen, setIsQuickNavOpen] = useState(false);

    // Fetch usage data to check canCreate (follows same pattern as MonitoringPage)
    interface ImageUsageResponse {
        usage: {
            canCreate: {
                imageOptimizations: boolean;
                imageOptimizationsReason?: string;
            };
        };
    }

    const { data: usageData, isLoading: isUsageLoading } = useQuery<ImageUsageResponse, Error>({
        queryKey: ['image-optimization-usage'],
        queryFn: async () => {
            if (!apiClient) throw new Error('API client not available');
            return await apiClient.get<ImageUsageResponse>('/images/usage');
        },
        enabled: !!user && !authIsLoading && !!apiClient,
        staleTime: 30000,
        retry: 1,
    });

    // Extract canCreate from usage data (default to false for safety - block access when unknown)
    const canCreate = usageData?.usage?.canCreate?.imageOptimizations ?? false;
    const canCreateReason = usageData?.usage?.canCreate?.imageOptimizationsReason;

    // Fetch all image optimization tasks
    const { data: tasksData, isLoading: isTasksLoading, refetch: refetchTasks } = useQuery<{ tasks: Task[] }, Error>({
        queryKey: ['image-optimization-tasks'],
        queryFn: async () => {
            if (!apiClient) throw new Error('API client not available');
            const response = await apiClient.get<unknown>('/tasks?type=image_optimization');
            const allTasks = extractTasksArray(response);
            return { tasks: allTasks };
        },
        enabled: !!user && !authIsLoading && !!apiClient,
        staleTime: 60000,
        retry: 1,
        retryDelay: 5000,
        refetchOnWindowFocus: false,
    });

    const { addTaskToDeleteQueue, queuedDeleteTasks } = useDeleteQueue();
    const tasks = useMemo(() => {
        const allTasks = tasksData?.tasks || [];
        return allTasks.filter(t => !queuedDeleteTasks.some(qt => qt.id === t.id && qt.status !== 'failed'));
    }, [tasksData, queuedDeleteTasks]);

    const selectedTask = tasks.find(t => t.id === selectedTaskId) || null;

    // Handle navigation state updates (if URL changes while component is mounted)
    useEffect(() => {
        const state = location.state as { taskId?: string; selectedTask?: Task };
        const searchParams = new URLSearchParams(location.search);
        const taskIdFromUrl = searchParams.get('taskId');

        const isQueuedForDelete = (id: string) => queuedDeleteTasks.some(t => t.id === id && t.status !== 'failed');

        if (state?.taskId && state.taskId !== selectedTaskId) {
            if (!isQueuedForDelete(state.taskId)) {
                setSelectedTaskId(state.taskId);
                setShowCreateForm(false);
            }
        } else if (taskIdFromUrl && taskIdFromUrl !== selectedTaskId) {
            if (!isQueuedForDelete(taskIdFromUrl)) {
                setSelectedTaskId(taskIdFromUrl);
                setShowCreateForm(false);
            }
        } else if (state?.selectedTask?.type === 'image_optimization' && state.selectedTask.id !== selectedTaskId) {
            if (!isQueuedForDelete(state.selectedTask.id)) {
                setSelectedTaskId(state.selectedTask.id);
                setShowCreateForm(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state, location.search, queuedDeleteTasks]);

    // Auto-select first task if none selected and tasks exist
    useEffect(() => {
        // Double check URL params to avoid race conditions even with lazy init
        const searchParams = new URLSearchParams(location.search);
        const hasUrlId = !!searchParams.get('taskId');
        const isQueuedForDelete = (id: string) => queuedDeleteTasks.some(t => t.id === id && t.status !== 'failed');

        if (!selectedTaskId && !hasUrlId && tasks.length > 0 && !showCreateForm) {
            // Extra safety check: ensure the candidate task is not in the delete queue
            // (Even though 'tasks' is filtered, this double-check prevents race conditions)
            const candidateId = tasks[0].id;
            if (!isQueuedForDelete(candidateId)) {
                setSelectedTaskId(candidateId);
            }
        }
    }, [tasks, selectedTaskId, showCreateForm, location.search, queuedDeleteTasks]);

    // Keyboard shortcut for Quick Nav (Ctrl+K / Cmd+K)
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setIsQuickNavOpen(true);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleFormClose = (newTaskId?: string) => {
        setShowCreateForm(false);
        refetchTasks();
        queryClient.invalidateQueries({ queryKey: ['image-tasks-nav'] });
        if (newTaskId) {
            setSelectedTaskId(newTaskId);
        }
    };

    const handleDelete = (id: string, title?: string) => {
        // Clear state and URL FIRST, before queuing, to ensure immediate UI reset
        if (selectedTaskId === id) {
            setSelectedTaskId(null);
            // Clear URL to prevent auto-reselection of deleted task
            navigate(location.pathname, { replace: true, state: null });
        }

        // Then add to delete queue
        addTaskToDeleteQueue({
            id,
            title: title || 'Image Optimization',
            type: 'image_optimization',
            status: 'open',
            created_at: new Date().toISOString()
        } as Task);

        // Trigger refetch to update list in background
        refetchTasks();
        queryClient.invalidateQueries({ queryKey: ['image-tasks-nav'] });
    };

    const handleTaskSelect = (taskId: string) => {
        setSelectedTaskId(taskId);
        setShowCreateForm(false);
        setIsQuickNavOpen(false);
    };

    return (
        <>
            <div className="container mx-auto p-4 md:p-6 space-y-4 md:space-y-6 max-w-full">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                    <div>
                        <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
                            <ImageIcon className="w-6 h-6" />
                            Image Optimization
                        </h1>
                        <p className="text-muted-foreground text-sm md:text-base">
                            Optimize images for better web performance
                        </p>
                    </div>
                    {!showCreateForm && (
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm" // Matches New Optimization size
                                onClick={() => setIsQuickNavOpen(true)}
                                className="hidden md:flex gap-1.5"
                            >
                                <Command className="h-4 w-4" />
                                Navigate
                                <kbd className="pointer-events-none h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 flex">⌘K</kbd>
                            </Button>
                            <Button
                                onClick={() => setShowCreateForm(true)}
                                disabled={!canCreate || isUsageLoading || (user?.user_type === 'unsubscribed')}
                                title={(!canCreate || (user?.user_type === 'unsubscribed')) ? (canCreateReason || 'Image optimization not available on your plan') : undefined}
                            >
                                <PlusCircle className="mr-2 h-4 w-4" />
                                New Optimization
                            </Button>
                        </div>
                    )}
                </div>

                {/* Main Content - View Switching like MonitoringPage */}
                {showCreateForm ? (
                    // Show Create Form (full page, not modal)
                    <ImageOptimizationForm
                        onSuccess={(newTaskId) => handleFormClose(newTaskId)}
                        onCancel={() => setShowCreateForm(false)}
                    />
                ) : (
                    <>
                        {/* Task List / Empty State handling */}
                        {isTasksLoading ? (
                            <Skeleton className="h-10 w-full max-w-sm mb-6" />
                        ) : !selectedTaskId && tasks.length === 0 ? (
                            <div className="text-center py-12 border-2 border-dashed rounded-lg">
                                <div className="rounded-full bg-primary/10 p-4 mb-4 mx-auto w-fit">
                                    <ImageIcon className="h-8 w-8 text-primary" />
                                </div>
                                <h3 className="text-lg font-semibold">No Optimization Tasks</h3>
                                <p className="text-muted-foreground mt-2">
                                    Click "New Optimization" to start optimizing your images.
                                </p>
                            </div>
                        ) : null}

                        {/* Task Detail View */}
                        {selectedTaskId && !queuedDeleteTasks.some(t => t.id === selectedTaskId) && (
                            <ImageOptimizationTaskView
                                taskId={selectedTaskId}
                                onDelete={handleDelete}
                            />
                        )}
                    </>
                )}
            </div>

            {/* Quick Nav Modal */}
            <ImageTaskQuickNav
                open={isQuickNavOpen}
                onOpenChange={setIsQuickNavOpen}
                currentTaskId={selectedTaskId || undefined}
                onTaskSelect={handleTaskSelect}
                tasks={tasks} // Pass the parsed and filtered tasks
            />
        </>
    );
}

export default ImageOptimizationPage;
